import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Header } from "@/components/Header";
import { CartSheet } from "@/components/CartSheet";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Lots from "@/pages/Lots";
import Deals from "@/pages/Deals";
import LotBuys from "@/pages/LotBuys";
import LotDetail from "@/pages/LotDetail";
import Favorites from "@/pages/Favorites";
import MyOrders from "@/pages/MyOrders";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ResumeCart from "@/pages/ResumeCart";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import FAQ from "@/pages/FAQ";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import { CookieConsent, openCookiePreferences } from "@/components/CookieConsent";

function Footer() {
  return (
    <footer className="border-t-2 border-ink mt-16 bg-ink text-paper">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 flex flex-col sm:flex-row justify-between gap-6">
        <div className="max-w-md">
          <div className="font-heading font-black text-xl tracking-tighter">STR SURPLUS</div>
          <div className="font-mono text-[11px] text-subtle uppercase tracking-widest mt-1">Operator Liquidation · Fort Lauderdale, FL</div>
          <p className="font-mono text-[11px] text-subtle leading-relaxed mt-3">
            Surplus, liquidation &amp; auction-lot goods for <strong className="text-paper">short-term rental owners, operators and property managers</strong> — restock your Airbnb or vacation rental with below-list, condition-graded small appliances, coffee makers, linens and electronics. Ships UPS Ground nationwide · free local pickup.
          </p>
        </div>
        <div className="font-mono text-[11px] text-subtle uppercase tracking-widest flex flex-col gap-2">
          <a href="/" className="hover:text-paper transition-colors">Shop Catalog</a>
          <a href="/deals" className="hover:text-paper transition-colors">Liquidation Deals</a>
          <a href="/lot-buys" className="hover:text-paper transition-colors">Lot Buys &amp; Bundles</a>
          <a href="/lots" className="hover:text-paper transition-colors">Auction Lots</a>
          <a href="/faq" className="hover:text-paper transition-colors">FAQ</a>
        </div>
      </div>
      <div className="border-t border-paper/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex flex-col sm:flex-row justify-between gap-3 font-mono text-[10px] uppercase tracking-widest text-subtle">
          <div data-testid="footer-legal-address">© {new Date().getFullYear()} STR Surplus LLC · Fort Lauderdale, FL 33304 · <a href="mailto:orders@strsurplus.com" className="hover:text-paper transition-colors">orders@strsurplus.com</a></div>
          <div className="flex gap-4 items-center">
            <a href="/privacy" data-testid="footer-privacy" className="hover:text-paper transition-colors">Privacy Policy</a>
            <a href="/terms" data-testid="footer-terms" className="hover:text-paper transition-colors">Terms of Service</a>
            <button data-testid="footer-cookies" onClick={openCookiePreferences} className="hover:text-paper transition-colors uppercase tracking-widest">Cookie Preferences</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <FavoritesProvider>
              <Header />
              <CartSheet />
              <main className="min-h-[60vh]">
                <Routes>
                  <Route path="/" element={<Catalog />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/lots" element={<Lots />} />
                  <Route path="/deals" element={<Deals />} />
                  <Route path="/lot-buys" element={<LotBuys />} />
                  <Route path="/lot/:lotName" element={<LotDetail />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/favorites" element={<Favorites />} />
                  <Route path="/orders" element={<MyOrders />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/cart/resume" element={<ResumeCart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin" element={<Admin />} />
                </Routes>
              </main>
              <Footer />
              <CookieConsent />
              <Toaster position="top-right" />
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
